import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import easydropdown from 'easydropdown'

function ListingsSearch({ categories, filters, handleFilter }) {
  const categoryRef = useRef()
  const subcategoryRef = useRef()
  const categoryEddRef = useRef()
  const subcategoryEddRef = useRef()
  const [searchOpen, setSearchOpen] = useState(window.matchMedia('(min-width: 768px)').matches)
  const [searchTerm, setSearchTerm] = useState(filters.search)
  const [subcategories, setSubcategories] = useState([])

  useEffect(() => {
    if (categoryRef.current) {
      const edd = easydropdown(categoryRef.current, {
        behavior: {
          openOnFocus: true,
          maxVisibleItems: 12,
          liveUpdates: true,
        },
      })

      categoryEddRef.current = edd

      categoryRef.current.addEventListener('change', (e) => {
        const selectedCategory = categories.find((x) => x.id === parseInt(e.target.value, 10))

        handleFilter(e.target.name, e.target.value)

        if (selectedCategory) {
          setSubcategories(selectedCategory.subcategories)
        }
      })
    }

    return () => {
      categoryEddRef.current.destroy()
    }
  }, [categoryRef])

  useEffect(() => {
    if (subcategoryRef.current) {
      const edd = easydropdown(subcategoryRef.current, {
        behavior: {
          openOnFocus: true,
          maxVisibleItems: 12,
          liveUpdates: true,
        },
      })

      subcategoryEddRef.current = edd

      subcategoryRef.current.addEventListener('change', (e) => {
        handleFilter(e.target.name, e.target.value)
      })
    }

    return () => {
      subcategoryEddRef.current.destroy()
    }
  }, [subcategoryRef])

  useEffect(() => {
    setSearchTerm(filters.search)
  }, [filters])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (searchTerm !== '') {
      handleFilter('search', searchTerm)
    }
  }

  return (
    <>
      <button
        type="button"
        className="inline-flex items-center font-medium hover:text-brand-red transition-colors md:hidden"
        onClick={() => setSearchOpen(!searchOpen)}
      >
        Filters
        {!searchOpen && (
          <svg viewBox="0 0 24 24" className="flex-shrink-0 w-5 h-5 ml-3">
            <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
          </svg>
        )}
        {searchOpen && (
          <svg viewBox="0 0 24 24" className="flex-shrink-0 w-5 h-5 ml-3">
            <path fill="currentColor" d="M19,13H5V11H19V13Z" />
          </svg>
        )}
      </button>

      <div
        id="listings-search"
        className={`mt-4 bg-white md:p-4 md:shadow-xl md:-mt-24 md:-mx-8 lg:p-8 lg:-mt-28 xl:mx-0 xl:p-10 xl:-mt-40 relative z-20 ${
          searchOpen ? '' : 'hidden'
        }`}
      >
        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-12 xl:gap-2">
          <div className="relative md:col-span-12 lg:col-span-4 xl:col-span-4">
            <input
              type="search"
              name="search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
              }}
              placeholder="What are you looking for?"
              className="block w-full h-full px-4 py-3 pr-10 font-normal placeholder-gray-500 border border-gray-300 hover:border-gray-400"
            />
            <div className="absolute top-0 right-0 mt-4 mr-4 text-gray-400" aria-label="Search">
              <svg viewBox="0 0 20 20" className="w-5 h-5">
                <path
                  d="M18.98 19.863l.883-.883a.47.47 0 000-.664l-4.742-4.742a.464.464 0 00-.332-.137h-.516A8.124 8.124 0 008.125 0 8.124 8.124 0 000 8.125a8.124 8.124 0 0013.437 6.148v.516c0 .125.051.242.137.332l4.742 4.742a.47.47 0 00.664 0zM8.125 14.375a6.248 6.248 0 01-6.25-6.25 6.248 6.248 0 016.25-6.25 6.248 6.248 0 016.25 6.25 6.248 6.248 0 01-6.25 6.25z"
                  fill="currentColor"
                  fillRule="nonzero"
                />
              </svg>
            </div>
          </div>

          <div className="md:col-span-6 lg:col-span-4 xl:col-span-3 dark-placeholder">
            <select name="category" ref={categoryRef} value={filters.category} onChange={() => {}}>
              <option value="" data-placeholder>
                Select a Category
              </option>

              {categories.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.title}
                </option>
              ))}
            </select>
          </div>

          <div
            className={`md:col-span-6 lg:col-span-4 xl:col-span-3 ${
              subcategories.length === 0 ? 'opacity-50' : 'dark-placeholder'
            }`}
          >
            <select
              name="subcategory"
              ref={subcategoryRef}
              value={filters.subcategory}
              onChange={() => {}}
              disabled={subcategories.length === 0}
            >
              <option value="" data-placeholder>
                Select a Subcategory
              </option>

              {subcategories.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.title}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="items-center w-full btn btn-fill md:col-span-12 xl:col-span-2 md:text-sm xl:text-base"
          >
            Search
          </button>
        </form>
      </div>
    </>
  )
}

ListingsSearch.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object),
  filters: PropTypes.object,
  handleFilter: PropTypes.func,
}

export default ListingsSearch
